import {
  ApiNoteInProgress,
  usePostByTenantNoteAndNoteIdInstructionApproveCommitmentClientMutation,
  usePostByTenantNoteAndNoteIdInstructionApproveCommitmentMutation,
  usePostByTenantNoteAndNoteIdInstructionRejectMutation
} from 'api/rtk-api'
import clsx from 'clsx'
import { ConfirmUsernameField, LabelValueRow, LinkButton, Tabs } from 'components/core-v2'
import Icon from 'components/core-v2/atoms/Icon'
import IconButton from 'components/core-v2/molecules/iconButton'
import { intersection } from 'lodash'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useUser } from 'utilities/hooks/useUser'

import { TENANT } from '../../../const'
import { useOnSuccess } from '../utilities/useOnSuccess'
import classes from './activation-tab.module.scss'
import { ConfirmationLink } from './confirmation-link'
import { ConfirmationModal } from './confirmation-modal'
import { WaitingOnRoleAlert } from './waiting-on-role-alert'

const BASE_AUTHORISED_ROLES = ['Administrator', 'Sales', 'BankAdmin']
interface Props {
  inProgressNote: ApiNoteInProgress
}
export const CommitmentTab = ({ inProgressNote }: Props) => {
  const { data: user } = useUser()
  const { noteId } = useParams()
  const form = useForm()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const isStepOne = inProgressNote.noteStatus === 'PendingCommitment'
  const startingTab = isStepOne ? 'business-unit' : 'client'

  const [tab, setTab] = useState(startingTab)

  const onSuccess = useOnSuccess()

  const [submitBusinessUnitApproval, { isLoading: isBULoading }] =
    usePostByTenantNoteAndNoteIdInstructionApproveCommitmentMutation()
  const [submitClientApproval, { isLoading: isClientLoading }] =
    usePostByTenantNoteAndNoteIdInstructionApproveCommitmentClientMutation()
  const [submitRejection] = usePostByTenantNoteAndNoteIdInstructionRejectMutation()

  const isLoading = isBULoading || isClientLoading

  if (!user) return null

  const status = inProgressNote.noteStatus
  const isCurrentStage = status === 'PendingClientCommitment' || status === 'PendingCommitment'
  const AUTHORISED_ROLES =
    status === 'PendingCommitment'
      ? [...BASE_AUTHORISED_ROLES, 'Editor']
      : [...BASE_AUTHORISED_ROLES, 'Client']
  const canUserCompleteStep = intersection(user.roles, AUTHORISED_ROLES).length > 0

  const isInteractive =
    canUserCompleteStep &&
    (tab === 'business-unit'
      ? status === 'PendingCommitment'
      : status === 'PendingClientCommitment')

  const onSubmit = async () => {
    const doesPassValidation = await form.trigger()
    if (doesPassValidation) {
      if (tab === 'business-unit') {
        setIsModalOpen(true)
      } else {
        const response = await submitClientApproval({
          tenant: TENANT,
          noteId
        })
        // Success
        if ('data' in response) {
          onSuccess()
        }
      }
    }
  }

  const onSubmitClick = async () => {
    const response = await submitBusinessUnitApproval({
      tenant: TENANT,
      noteId
    })
    // Success
    if ('data' in response) {
      onSuccess()
    }
  }

  return (
    <>
      <Tabs.List spacing="sm">
        <Tabs.Tab
          isSelected={tab === 'business-unit'}
          value="business-unit"
          onClick={() => setTab('business-unit')}
        >
          a. Business unit commitment
        </Tabs.Tab>
        <Tabs.Tab
          className={clsx({
            [classes.readOnly]: isStepOne
          })}
          isSelected={tab === 'client'}
          value="client"
          onClick={isStepOne ? null : () => setTab('client')}
        >
          b. {inProgressNote.client} commitment
        </Tabs.Tab>
      </Tabs.List>
      <div className={classes.tabPanel}>
        {!canUserCompleteStep && isCurrentStage && <WaitingOnRoleAlert />}
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <p>Please download and review the trade confirmations</p>
            <div className={classes.tradeWrapper}>
              {inProgressNote.associatedTrades.map(trade => (
                <LabelValueRow
                  key={trade.accountId}
                  label={trade.accountName}
                  value={<ConfirmationLink tradeId={trade.accountId} />}
                />
              ))}
            </div>
            {isInteractive && (
              <>
                <p className="mb-8">
                  Commitment must be provided no later than HH:MM on DD MM YYYY, otherwise the trade
                  will be rejected and will need to be resubmitted.
                </p>
                <ConfirmUsernameField
                  id="usernameConfirmation"
                  label="To approve the trade, enter your username below and click on the button"
                />
                <div className={classes.buttonWrapper}>
                  <IconButton
                    icon={<Icon name="check" size="small" />}
                    iconPosition="left"
                    isLoading={isLoading}
                    operationType="progress"
                    text="Approve"
                    type="submit"
                  />
                  <LinkButton
                    type="button"
                    onClick={() => submitRejection({ tenant: TENANT, noteId })}
                  >
                    Reject
                  </LinkButton>
                </div>
              </>
            )}
          </form>
          <ConfirmationModal
            isLoading={isLoading}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={form.handleSubmit(onSubmitClick)}
          />
        </FormProvider>
      </div>
    </>
  )
}
